import { classifyRequestFailure, fetchWithTimeout } from "../utils/fetchWithTimeout";
export interface AuthUser {
  id: number;
  username: string;
  role: string;
  must_change_password: boolean;
  /** Optionaler Serverhinweis, falls das Backend Initialpasswort und späteren Admin-Reset unterscheidet. */
  password_change_reason?: "initial" | "reset" | null;
}

export type ServerConnectionState = "checking" | "online" | "slow" | "offline";

interface ErrorResponse {
  detail?: unknown;
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json() as ErrorResponse;
    return typeof data.detail === "string" && data.detail.trim()
      ? data.detail
      : fallback;
  } catch {
    return fallback;
  }
}

const LAST_USER_KEY = "materialentnahme:last-auth-user";
const PENDING_SERVER_LOGOUT_KEY = "materialentnahme:pending-server-logout:v1";
let pendingServerLogoutRequest: Promise<boolean> | null = null;

function markPendingServerLogout(): void {
  try { window.localStorage.setItem(PENDING_SERVER_LOGOUT_KEY, new Date().toISOString()); } catch { /* optional */ }
}

export function hasPendingServerLogout(): boolean {
  try { return Boolean(window.localStorage.getItem(PENDING_SERVER_LOGOUT_KEY)); } catch { return false; }
}

export function clearPendingServerLogout(): void {
  try { window.localStorage.removeItem(PENDING_SERVER_LOGOUT_KEY); } catch { /* optional */ }
}

export async function completePendingServerLogout(timeoutMs = 2500): Promise<boolean> {
  if (!hasPendingServerLogout()) return true;
  if (pendingServerLogoutRequest) return pendingServerLogoutRequest;

  pendingServerLogoutRequest = (async () => {
    try {
      const response = await fetchWithTimeout("/api/auth/logout", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
      }, timeoutMs);
      if (!response.ok) return false;
      clearPendingServerLogout();
      return true;
    } catch {
      return false;
    }
  })();

  try {
    return await pendingServerLogoutRequest;
  } finally {
    pendingServerLogoutRequest = null;
  }
}

export async function settlePendingServerLogoutBeforeAuthentication(): Promise<void> {
  if (!hasPendingServerLogout()) return;
  const completed = await completePendingServerLogout(3000);
  if (!completed) {
    throw new Error("Die vorherige Abmeldung ist serverseitig noch nicht bestätigt. Stelle die Serververbindung her und versuche die Anmeldung erneut.");
  }
}

export function rememberAuthUser(user: AuthUser): AuthUser {
  clearPendingServerLogout();
  try { window.localStorage.setItem(LAST_USER_KEY, JSON.stringify(user)); } catch { /* optional */ }
  try { window.sessionStorage.setItem(LAST_USER_KEY, JSON.stringify(user)); } catch { /* optional */ }
  return user;
}

export function getLastKnownAuthUser(): AuthUser | null {
  try {
    const raw = window.localStorage.getItem(LAST_USER_KEY) ?? window.sessionStorage.getItem(LAST_USER_KEY);
    return raw ? JSON.parse(raw) as AuthUser : null;
  } catch { return null; }
}

export function clearLastKnownAuthUser(): void {
  try { window.localStorage.removeItem(LAST_USER_KEY); } catch { /* optional */ }
  try { window.sessionStorage.removeItem(LAST_USER_KEY); } catch { /* optional */ }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  const response = await fetchWithTimeout("/api/auth/me", {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  }, 3000);

  if (response.status === 401) { clearLastKnownAuthUser(); return null; }
  if (!response.ok) {
    throw new Error(
      await errorMessage(response, `Session konnte nicht geprüft werden (${response.status}).`)
    );
  }

  return rememberAuthUser(await response.json() as AuthUser);
}

export async function loginUser(username: string, password: string): Promise<AuthUser> {
  try {
    await settlePendingServerLogoutBeforeAuthentication();
    const response = await fetchWithTimeout("/api/auth/login", {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    }, 8000);

    if (!response.ok) {
      throw new Error(
        await errorMessage(
          response,
          response.status === 401
            ? "Benutzername oder Passwort ist ungültig."
            : `Anmeldung fehlgeschlagen (${response.status}).`
        )
      );
    }

    return rememberAuthUser(await response.json() as AuthUser);
  } catch (error) {
    const kind = classifyRequestFailure(error);
    if (kind === "timeout") {
      throw new Error("Zeitüberschreitung bei der Anmeldung. Der Server kann erreichbar sein, antwortet aber derzeit zu langsam.", { cause: error });
    }
    if (kind === "network") {
      throw new Error("Anmeldung nicht möglich. Die Netzwerkverbindung zum Server ist derzeit nicht verfügbar.", { cause: error });
    }
    throw error;
  }
}

export async function checkServerConnection(): Promise<ServerConnectionState> {
  let lastKind: ReturnType<typeof classifyRequestFailure> = "unknown";
  for (const timeoutMs of [900, 2500] as const) {
    try {
      const response = await fetchWithTimeout("/api/status", {
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/json" },
      }, timeoutMs);
      // Auch HTTP 4xx/5xx beweist, dass der Server erreichbar ist. Der Zustand
      // beschreibt hier ausschließlich die Verbindung, nicht die Dienstgesundheit.
      void response;
      return "online";
    } catch (error) {
      lastKind = classifyRequestFailure(error);
      if (timeoutMs === 900) continue;
    }
  }
  return lastKind === "timeout" ? "slow" : "offline";
}


export async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<AuthUser> {
  try {
    const response = await fetchWithTimeout("/api/auth/password", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }, 5000);
    if (!response.ok) throw new Error(await errorMessage(response, "Passwort konnte nicht geändert werden."));
    return rememberAuthUser(await response.json() as AuthUser);
  } catch (error) {
    if (classifyRequestFailure(error) === "timeout") {
      throw new Error("Zeitüberschreitung bei der Serververbindung. Die Passwortänderung wurde nicht bestätigt.", { cause: error });
    }
    throw error;
  }
}


export async function logoutUser(): Promise<void> {
  // Der sichtbare Logout bleibt local-first, aber ein fehlgeschlagener Server-
  // Logout wird persistent vermerkt. Ein späterer App-Start darf die alte Cookie-
  // Sitzung dann nicht versehentlich wieder als angemeldet übernehmen.
  markPendingServerLogout();
  clearLastKnownAuthUser();
  void completePendingServerLogout(2500);
}
